/**
 * C5 — the Clients table as AgencyAnalytics' roll-up table: each client a
 * row, chosen metrics as columns (at most 25) for one period, and a totals
 * row. One roll-up query per source answers every client at once
 * (Dimension "Client"). The chosen columns and period are remembered in this
 * browser only; a blocked storage simply starts empty.
 */

import { useEffect, useMemo, useState } from 'react';

import { reportsApi, type Catalogue, type WidgetResult } from '../reports/reports-api.ts';

export interface ClientColumn {
  source: string;
  metric: string;
}

export const MAX_COLUMNS = 25;
const METRICS_PER_WIDGET = 8;
const STORAGE_KEY = 'synanalytics.client-columns';
const TIMEZONE = 'Africa/Johannesburg';

export const columnKey = (column: ClientColumn) => `${column.source}.${column.metric}`;

function remembered(): { columns: ClientColumn[]; preset: string } {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}') as {
      columns?: ClientColumn[];
      preset?: string;
    };
    return {
      columns: Array.isArray(saved.columns) ? saved.columns.slice(0, MAX_COLUMNS) : [],
      preset: typeof saved.preset === 'string' ? saved.preset : 'LAST_MONTH',
    };
  } catch {
    return { columns: [], preset: 'LAST_MONTH' };
  }
}

function remember(columns: ClientColumn[], preset: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ columns, preset }));
  } catch {
    // Storage blocked: the choice lasts for this visit only.
  }
}

/** One widget per source, at most 8 metrics each, every one broken down by client. */
function widgetsFor(columns: ClientColumn[]) {
  const bySource = new Map<string, string[]>();
  for (const column of columns)
    bySource.set(column.source, [...(bySource.get(column.source) ?? []), column.metric]);
  return [...bySource.entries()].flatMap(([source, metrics]) =>
    Array.from({ length: Math.ceil(metrics.length / METRICS_PER_WIDGET) }, (_, index) => ({
      id: `${source}#${index}`,
      source,
      metrics: metrics.slice(index * METRICS_PER_WIDGET, (index + 1) * METRICS_PER_WIDGET),
      dimension: 'client',
      limit: 50,
    })),
  );
}

export function useClientColumns(organisationId: string, catalogue: Catalogue | null) {
  const [state, setState] = useState(remembered);
  const [results, setResults] = useState<WidgetResult[] | null>(null);
  const valid = useMemo(
    () =>
      state.columns.filter((column) =>
        catalogue?.sources
          .find((source) => source.key === column.source)
          ?.metrics.some((metric) => metric.key === column.metric),
      ),
    [state.columns, catalogue],
  );
  const signature = JSON.stringify([valid, state.preset]);
  useEffect(() => {
    if (valid.length === 0) {
      setResults([]);
      return undefined;
    }
    let cancelled = false;
    setResults(null);
    reportsApi
      .query(organisationId, {
        business_unit_id: null,
        mode: 'LIVE',
        timezone: TIMEZONE,
        range: { preset: state.preset },
        widgets: widgetsFor(valid),
      })
      .then((data) => !cancelled && setResults(data.results))
      .catch(() => !cancelled && setResults([]));
    return () => {
      cancelled = true;
    };
    // The signature holds every input of the query.
  }, [organisationId, signature]);

  const update = (columns: ClientColumn[], preset: string) => {
    setState({ columns, preset });
    remember(columns, preset);
  };
  const find = (column: ClientColumn) =>
    results?.find(
      (result) =>
        result.id.startsWith(`${column.source}#`) &&
        result.metrics.some((metric) => metric.key === column.metric),
    );
  return {
    columns: valid,
    preset: state.preset,
    isLoading: valid.length > 0 && results === null,
    setColumns: (columns: ClientColumn[]) => update(columns.slice(0, MAX_COLUMNS), state.preset),
    setPreset: (preset: string) => update(state.columns, preset),
    valueOf: (clientName: string, column: ClientColumn): number | null =>
      find(column)?.rows?.find((row) => row.label === clientName)?.values[column.metric] ?? null,
    totalOf: (column: ClientColumn): number | null =>
      find(column)?.metrics.find((metric) => metric.key === column.metric)?.value ?? null,
  };
}
